import { useEffect, useState } from "react";

export interface TeamMemberFormState {
  full_name: string;
  email: string;
  role: "admin" | "staff";
  job_title: string;
  password?: string;
}



type Mode = "create" | "edit";

export default function TeamMemberModal({
  open,
  mode,
  initial,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode: Mode;
  initial?: Partial<TeamMemberFormState> | null;
  onClose: () => void;
  onSubmit: (payload: Partial<TeamMemberFormState> & { password?: string }) => Promise<void> | void;
}) {
  const title = mode === "create" ? "Invite Team Member" : "Edit Team Member";

  const [form, setForm] = useState<TeamMemberFormState>({
    full_name: "",
    email: "",
    role: "staff",
    job_title: "",
    password: "",
  });



  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setError(null);
    setSubmitting(false);

    if (initial) {
      setForm({
        full_name: initial.full_name || "",
        email: initial.email || "",
        role: initial.role || "staff",
        job_title: initial.job_title || "",
        password: "",
      });
    } else {
      setForm({
        full_name: "",
        email: "",
        role: "staff",
        job_title: "",
        password: "",
      });
    }

  }, [open, initial]);


  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      setSubmitting(true);

      // Only include password if provided and not empty
      const payload: any = {
        full_name: form.full_name,
        email: form.email,
        role: form.role,
        job_title: form.job_title || undefined,
      };

      if (form.password && form.password.trim()) {
        payload.password = form.password;
      }

      await onSubmit(payload);
      setSubmitting(false);
      onClose();
    } catch (err: any) {
      setSubmitting(false);
      setError(err?.message || "Failed to save team member");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-dark-panel border border-dark-border rounded-2xl p-6 max-w-md w-full animate-slide-up">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-white font-bold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {title}
            </h3>
            <p className="text-silver text-sm mt-1">
              {mode === "create" ? "Add a new team member to the platform." : "Update team member information."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/40 transition-colors cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block">
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Full Name</span>
            <input
              className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
              value={form.full_name}
              onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
              required
              placeholder="John Doe"
            />
          </label>

          <label className="block">
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Email</span>
            <input
              type="email"
              className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
              value={form.email}
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              required
              placeholder="john@example.com"
            />
          </label>

          <label className="block">
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Role</span>
            <select
              className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
              value={form.role}
              onChange={(e) => setForm((p) => ({ ...p, role: e.target.value as "admin" | "staff" }))}
            >
              <option value="staff">Staff</option>
              <option value="admin">Admin</option>
            </select>
          </label>

          <label className="block">
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Job Title</span>
            <input
              className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
              value={form.job_title}
              onChange={(e) => setForm((p) => ({ ...p, job_title: e.target.value }))}
              placeholder="Senior Developer"
            />
          </label>

          <label className="block">
            <span className="text-silver-dim text-xs font-semibold uppercase tracking-wider">Password</span>
            <input
              type="password"
              className="mt-1 w-full bg-dark-card border border-dark-border rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/50 transition-colors"
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              required={mode === "create"}
              placeholder={mode === "create" ? "Set a password" : "Leave blank to keep unchanged"}
            />
          </label>

          {error && <div className="text-cyber-orange text-sm">{error}</div>}


          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-dark-border text-silver hover:text-white hover:border-electric-blue/40 transition-all text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`flex-1 py-2.5 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                submitting ? "bg-electric-blue/40 text-deep-black cursor-not-allowed" : "bg-electric-blue text-deep-black hover:shadow-glow-blue"
              }`}
            >
              {submitting ? "Saving..." : mode === "create" ? "Invite" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}